import styled from "@emotion/styled";
import { ErrorMessage } from "@hookform/error-message";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack
} from "@mui/material";
import { EthiopianDatePicker } from "components/UI";
import { Controller, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { BsPlus, BsTrash } from "react-icons/bs";

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Label = styled.label`
  color: #5b5b5b;
  font-size: 12px;
  font-weight: 500;
`;

const Input = styled.input`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
`;

const Select = styled.select`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
  font-family: inherit;
`;

const HelperText = styled.span`
  font-size: 12px;
  color: ${({ theme, error }) =>
    error ? theme.palette.error.main : "inherit"};
`;

export const JobForm = ({ register, errors, control, watch, setValue }) => {
  const EMPLOYMENT_TYPE = [
    "FULL-TIME",
    "PART-TIME",
    "TEMPORARY",
    "CONTRACT",
    "FREELANCE",
    "INTERNSHIP",
    "SEASONAL",
    "REMOTE",
    "PERMANENT",
    "FLEXIBLE HOURS",
    "COMMISSION-BASED",
    "PROJECT-BASED",
    "JOB-SHARING",
    "VOLUNTEER",
    "ON-CALL",
    "CASUAL",
    "GIG ECONOMY",
    "ZERO-HOURS CONTRACT",
    "APPRENTICESHIP",
    "CO-OP (COOPERATIVE)"
  ];

  const { t } = useTranslation();

  const {
    fields: workExperiences,
    append: appendWorkExperience,
    remove: removeWorkExperience
  } = useFieldArray({
    control,
    name: "work_experience"
  });

  return (
    <Stack spacing={2}>
      <Field>
        <Label htmlFor="position">{t("HR.Job.Position")}</Label>
        <Input id="position" {...register("position")} />
        <ErrorMessage
          errors={errors}
          name="position"
          render={({ message }) => <HelperText error>{message}</HelperText>}
        />
      </Field>
      <Field>
        <Label htmlFor="employment_date">{t("HR.Job.EmploymentDate")}</Label>
        <EthiopianDatePicker
          value={watch("employment_date")}
          onChange={e => setValue("employment_date", e.target.value)}
        />
        <ErrorMessage
          errors={errors}
          name="employment_date"
          render={({ message }) => <HelperText error>{message}</HelperText>}
        />
      </Field>
      <Field>
        <Label htmlFor="employment_type">{t("HR.Job.EmploymentType")}</Label>
        <Select id="employment_type" {...register("employment_type")}>
          {EMPLOYMENT_TYPE.map(type => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </Select>
        <ErrorMessage
          errors={errors}
          name="employment_type"
          render={({ message }) => <HelperText error>{message}</HelperText>}
        />
      </Field>
      <FormControl>
        <FormLabel sx={{ fontSize: "12px" }}>
          {t("HR.Job.HaveWorkExperience")}
        </FormLabel>
        <Controller
          control={control}
          name="has_experience"
          render={({ field }) => (
            <RadioGroup row {...field}>
              <FormControlLabel
                value={true}
                control={<Radio size="small" />}
                label={t("form.common.yes")}
              />
              <FormControlLabel
                value={false}
                control={<Radio size="small" />}
                label={t("form.common.no")}
              />
            </RadioGroup>
          )}
        />
      </FormControl>
      {watch("has_experience") === "true" && (
        <Stack spacing={2}>
          {workExperiences.map((experience, index) => (
            <Box
              key={experience.id}
              sx={{
                position: "relative",
                p: 2,
                border: "1px solid #e0e0e0",
                borderRadius: 1
              }}
            >
              <IconButton
                size="small"
                sx={{ position: "absolute", right: 8, top: 8 }}
                onClick={() => removeWorkExperience(index)}
              >
                <BsTrash />
              </IconButton>
              <Stack spacing={2}>
                <Field>
                  <Label>{t("HR.Job.CompanyName")}</Label>
                  <Input
                    {...register(`work_experience.${index}.company_name`)}
                  />
                  <ErrorMessage
                    errors={errors}
                    name={`work_experience.${index}.company_name`}
                    render={({ message }) => (
                      <HelperText error>{message}</HelperText>
                    )}
                  />
                </Field>
                <Field>
                  <Label>{t("HR.Job.JobTitle")}</Label>
                  <Input {...register(`work_experience.${index}.job_title`)} />
                  <ErrorMessage
                    errors={errors}
                    name={`work_experience.${index}.job_title`}
                    render={({ message }) => (
                      <HelperText error>{message}</HelperText>
                    )}
                  />
                </Field>
                <Field>
                  <Label>{t("HR.Job.MonthlyIncome")}</Label>
                  <Input
                    type="number"
                    {...register(`work_experience.${index}.monthly_income`)}
                  />
                  <ErrorMessage
                    errors={errors}
                    name={`work_experience.${index}.monthly_income`}
                    render={({ message }) => (
                      <HelperText error>{message}</HelperText>
                    )}
                  />
                </Field>
                <Field>
                  <Label>{t("HR.Job.JobStartDate")}</Label>
                  <EthiopianDatePicker
                    value={watch(`work_experience.${index}.start_date`)}
                    onChange={e =>
                      setValue(
                        `work_experience.${index}.start_date`,
                        e.target.value
                      )
                    }
                  />
                  <ErrorMessage
                    errors={errors}
                    name={`work_experience.${index}.start_date`}
                    render={({ message }) => (
                      <HelperText error>{message}</HelperText>
                    )}
                  />
                </Field>
                <Field>
                  <Label>{t("HR.Job.JobEndDate")}</Label>
                  <EthiopianDatePicker
                    value={watch(`work_experience.${index}.end_date`)}
                    onChange={e =>
                      setValue(
                        `work_experience.${index}.end_date`,
                        e.target.value
                      )
                    }
                  />
                  <ErrorMessage
                    errors={errors}
                    name={`work_experience.${index}.end_date`}
                    render={({ message }) => (
                      <HelperText error>{message}</HelperText>
                    )}
                  />
                </Field>
              </Stack>
            </Box>
          ))}
          <Button
            type="button"
            onClick={() =>
              appendWorkExperience({
                company_name: "",
                job_title: "",
                monthly_income: "",
                start_date: null,
                end_date: null
              })
            }
            startIcon={<BsPlus />}
            variant="outlined"
            fullWidth
          >
            {t("HR.Job.AddWorkExperience")}
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
